import { PlatformConnectionSetup } from "../../platform-connection-setup";
import { type LegacyPlatform } from "../../utms/legacy-platforms";
import { Platform } from "../constants";
import { PlatformMetadataStore } from "../metadata";

import { AdRollDetails, AdRollSetup } from "./adroll";
import { AmazonAds, AmazonAdsDetails } from "./amazon-ads";
import { AmazonSeller, AmazonSellerDetails } from "./amazon-seller";
import { AppLovinDetails, AppLovinSetup } from "./applovin";
import { AttentiveDetails, AttentiveSetup } from "./attentive";
import { AvantLinkDetails, AvantLinkSetup } from "./avantlink";
import {
  BlissPointMediaDetails,
  BlissPointMediaSetup,
} from "./bliss-point-media";
import { CNN5ThingsDetails, CNN5ThingsSetup } from "./cnn5things";
import { CriteoDetails, CriteoSetup } from "./criteo";
import { GoogleDetails, GoogleSetup } from "./google-ads";
import { GrinDetails, GrinSetup } from "./grin";
import { ImpactRadiusDetails, ImpactRadiusSetup } from "./impact-radius";
import { KlaviyoDetails, KlaviyoSetup } from "./klaviyo";
import { LiveIntentDetails, LiveIntentSetup } from "./live-intent";
import { MetaDetails, MetaSetup } from "./meta";
import { MicrosoftDetails, MicrosoftSetup } from "./microsoft";
import { MNTNDetails, MNTNSetup } from "./mntn";
import { OtherDetails, OtherSetup } from "./other";
import { OutbrainDetails, OutbrainSetup } from "./outbrain";
import { PartnerizeDetails, PartnerizeSetup } from "./partnerize";
import { PinterestDetails, PinterestSetup } from "./pinterest";
import { type PlatformDetails } from "./platform-details";
import { PostscriptDetails, PostscriptSetup } from "./postscript";
import { RakutenDetails, RakutenSetup } from "./rakuten";
import {
  RetentionScienceDetails,
  RetentionScienceSetup,
} from "./retention-science";
import { RevcontentDetails, RevcontentSetup } from "./revcontent";
import { ShareASaleDetails, ShareASaleSetup } from "./share-a-sale";
import { SnapchatDetails, SnapchatSetup } from "./snapchat";
import { StackAdaptDetails, StackAdaptSetup } from "./stack-adapt";
import { TaboolaDetails, TaboolaSetup } from "./taboola";
import { TatariDetails, TatariSetup } from "./tatari";
import { TikTokDetails, TikTokSetup } from "./tiktok";
import { TradeDeskDetails, TradeDeskSetup } from "./trade-desk";
import { TwitterDetails, TwitterSetup } from "./twitter";
import { YahooDSPDetails, YahooDSPSetup } from "./yahoo-dsp";

const _details: {
  [key in Platform]:
    | {
        details: PlatformDetails | undefined;
        setup:
          | React.FC
          | React.FC<{
              connectionContent: React.ReactNode;
              legacyPlatforms?: LegacyPlatform[];
            }>;
      }
    | undefined;
} = {
  [Platform.AD_ROLL]: {
    details: AdRollDetails,
    setup: AdRollSetup,
  },
  [Platform.AMAZON_ADS]: {
    details: AmazonAdsDetails,
    setup: AmazonAds, // Amazon is special cased because it has 2 connections.
  },
  [Platform.AMAZON_SELLER]: {
    // Amazon is special cased to use AmazonAds for connecting both ads and seller.
    details: AmazonSellerDetails,
    setup: AmazonSeller,
  },
  [Platform.ATTENTIVE]: {
    details: AttentiveDetails,
    setup: AttentiveSetup,
  },
  [Platform.AVANT_LINK]: {
    details: AvantLinkDetails,
    setup: AvantLinkSetup,
  },
  [Platform.BLISS_POINT_MEDIA]: {
    details: BlissPointMediaDetails,
    setup: BlissPointMediaSetup,
  },
  [Platform.CNN5_THINGS]: {
    details: CNN5ThingsDetails,
    setup: CNN5ThingsSetup,
  },
  [Platform.CRITEO]: {
    details: CriteoDetails,
    setup: CriteoSetup,
  },
  [Platform.GOOGLE]: {
    details: GoogleDetails,
    setup: GoogleSetup,
  },
  [Platform.GRIN]: {
    details: GrinDetails,
    setup: GrinSetup,
  },
  [Platform.IMPACT_RADIUS]: {
    details: ImpactRadiusDetails,
    setup: ImpactRadiusSetup,
  },
  [Platform.KLAVIYO]: {
    details: KlaviyoDetails,
    setup: KlaviyoSetup,
  },
  [Platform.LIVE_INTENT]: {
    details: LiveIntentDetails,
    setup: LiveIntentSetup,
  },
  [Platform.MICROSOFT]: {
    details: MicrosoftDetails,
    setup: MicrosoftSetup,
  },
  [Platform.META]: {
    details: MetaDetails,
    setup: MetaSetup,
  },
  [Platform.MNTN]: {
    details: MNTNDetails,
    setup: MNTNSetup,
  },
  [Platform.OUTBRAIN]: {
    details: OutbrainDetails,
    setup: OutbrainSetup,
  },
  [Platform.PARTNERIZE]: {
    details: PartnerizeDetails,
    setup: PartnerizeSetup,
  },
  [Platform.PINTEREST]: {
    details: PinterestDetails,
    setup: PinterestSetup,
  },
  [Platform.POSTSCRIPT]: {
    details: PostscriptDetails,
    setup: PostscriptSetup,
  },
  [Platform.RAKUTEN]: {
    details: RakutenDetails,
    setup: RakutenSetup,
  },
  [Platform.RETENTION_SCIENCE]: {
    details: RetentionScienceDetails,
    setup: RetentionScienceSetup,
  },
  [Platform.REVCONTENT]: {
    details: RevcontentDetails,
    setup: RevcontentSetup,
  },
  [Platform.SHARE_A_SALE]: {
    details: ShareASaleDetails,
    setup: ShareASaleSetup,
  },
  [Platform.SNAPCHAT]: {
    details: SnapchatDetails,
    setup: SnapchatSetup,
  },
  [Platform.STACK_ADAPT]: {
    details: StackAdaptDetails,
    setup: StackAdaptSetup,
  },
  [Platform.TABOOLA]: {
    details: TaboolaDetails,
    setup: TaboolaSetup,
  },
  [Platform.TATARI]: {
    details: TatariDetails,
    setup: TatariSetup,
  },
  [Platform.TIKTOK]: {
    details: TikTokDetails,
    setup: TikTokSetup,
  },
  [Platform.TRADE_DESK]: {
    details: TradeDeskDetails,
    setup: TradeDeskSetup,
  },
  [Platform.TWITTER]: {
    details: TwitterDetails,
    setup: TwitterSetup,
  },
  [Platform.APPLOVIN]: {
    details: AppLovinDetails,
    setup: AppLovinSetup,
  },
  [Platform.YAHOO_DSP]: {
    details: YahooDSPDetails,
    setup: YahooDSPSetup,
  },
  [Platform.OTHER]: {
    details: OtherDetails,
    setup: OtherSetup,
  },
  [Platform.UNKNOWN]: undefined,
};

export const PlatformSetup = ({
  platform,
  integrationDetails,
  toggleIsConfigured,
  legacyPlatforms,
}: {
  platform: Platform;
  integrationDetails: any;
  toggleIsConfigured: (platform: Platform) => (isConfigured: boolean) => void;
  legacyPlatforms?: LegacyPlatform[];
}) => {
  const Setup = _details[platform]?.setup;
  return Setup ? (
    <Setup
      connectionContent={
        <PlatformConnectionSetup
          connectionType={PlatformMetadataStore.get(platform).connectionType}
          integrationDetails={integrationDetails}
          toggleIsConfigured={toggleIsConfigured(platform)}
        />
      }
      legacyPlatforms={legacyPlatforms}
    />
  ) : null;
};

const DEFAULT_NB_OFFICIAL_DOC =
  "https://docs.northbeam.io/docs/tracking-for-other-platforms";

export function getPlatformDetails(platform: Platform) {
  const additionalDetails = _details[platform]?.details || {
    northbeamDocUrl: "",
    officialDocUrl: "",
    additionalDetails: [],
  };
  additionalDetails.northbeamDocUrl =
    additionalDetails.northbeamDocUrl || DEFAULT_NB_OFFICIAL_DOC;
  return additionalDetails;
}
